<template>
  <div v-if="activity" class="activity" :class="'activity-' + tone" :title="fullText">
    <span class="dot" />
    <span class="text">
      <span class="head">
        <span class="label">{{ label }}</span>
        <span class="body">{{ content }}</span>
      </span>
      <span v-if="meta" class="meta">
        <span v-if="momentText" class="moment">{{ momentText }}</span>
        <span v-if="momentText && durationText" class="sep">|</span>
        <span v-if="durationText" class="duration">{{ durationText }}</span>
      </span>
    </span>
  </div>
</template>

<script>
/**
 * 好友列表昵称下方的动态行：好友「正在做什么」或「上一次做了什么」。
 *
 * 状态用不同颜色区分：
 * - RUNNING（真正在做）：主色绿 + 呼吸圆点，强调「此刻正在发生」
 * - 记录但此刻还落在它的时间区间内：同样按「在做」强调（绿 + 呼吸圆点），
 *   只是前缀写成「在做(记录)：」—— 提示此刻正在这块里，但如实标出它是记录而不是进行中的块
 * - 已越过结束时间（上一次）：中性灰绿，弱化为「历史信息」
 *
 * 固定两行展示：
 *   上次：开始看论文！！
 *   结束于17:30 | 用时1小时29分钟
 *
 * 第二行口径按时间块所处阶段区分：
 * - 正在做（RUNNING，无实际结束时间）→「开始于17:30 | 用时1小时29分钟」，时长算到此刻
 * - 记录 / 计划且当前还没到结束时间 → 同上（仍在区间内，时长算到此刻）
 * - 记录 / 计划且当前已过结束时间 →「结束于17:30 | 用时1小时29分钟」，时长为整段
 * 时长不足 1 小时只给分钟（「35分钟」），不足 1 分钟给「不到1分钟」。
 *
 * 第一行内容超长时省略，第二行时长在极窄栏下省略，完整信息始终在 title 里。
 */
const DAY_MS = 24 * 60 * 60 * 1000
const MINUTE_MS = 60 * 1000

export default {
  name: 'FriendActivityLine',
  props: {
    /** 好友动态对象：{ status, content, projectName, projectId, startTime, endTime } */
    activity: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      /**
       * 「此刻」的毫秒基准。
       * - 0 → 不干预，计算时直接用 Date.now()（「上次」这类静态行行为不变）；
       * - 非 0 → 由「正在做」期间的定时器每 30s 推进一次，
       *   让「已用时 1小时3分钟」跟着真实时间走（否则 computed 会一直缓存挂载那一刻的值，
       *   好友页靠 60s 轮询刷新，自己的卡片只在版本变化时才重拉，看起来就像停住了）。
       */
      nowMs: 0
    }
  },
  created() {
    this.syncTicker()
  },
  beforeDestroy() {
    this.stopTicker()
  },
  watch: {
    // 状态可能在原地变化（开始做 / 结束做 / 走出区间），计时器要跟着起停
    liveKind() {
      this.syncTicker()
    }
  },
  computed: {
    kind() {
      return this.activity && this.activity.status === 'RUNNING' ? 'running' : 'done'
    },
    /**
     * 实时状态（决定前缀文案与配色）：
     * - 'running'     → RUNNING，真正在做
     * - 'live-record' → 记录（FINISHED），但当前时刻还落在它的区间内 → 也提示「在做」
     * - 'done'        → 记录且已越过结束时间 → 「上次」
     *
     * ⚠️ 依赖 timeInfo.ongoing，而 timeInfo 依赖 nowMs —— 计时器推进 nowMs 会让
     *    越过结束时间的那一刻自动从「在做(记录)」回落成「上次」，无需重新拉数据。
     */
    liveKind() {
      if (this.kind === 'running') return 'running'
      const info = this.timeInfo
      return info && info.ongoing ? 'live-record' : 'done'
    },
    /** 配色档位：只要「在做」（含记录仍在区间内）就用强调色，否则弱化为历史信息 */
    tone() {
      return this.liveKind === 'done' ? 'done' : 'running'
    },
    /**
     * 时间后缀信息：{ moment, ongoing, duration }
     * - ongoing=true：还在区间内（正在做，或计划/记录尚未到结束时间）→ 时刻取开始时间，时长算到此刻
     * - ongoing=false：已经越过结束时间 → 时刻取结束时间，时长是整段
     * 任一时间戳缺失时对应字段留空，文案自然退化成只有内容。
     *
     * ⚠️ ongoing 的判定必须「先看状态、再看结束时间」：
     *    记录（FINISHED）理论上一定带 endTime，万一缺失也不能当成「还在做」——
     *    否则会拿 startTime 一路算到此刻，显示成「已做 8 小时」这种假信息。
     */
    timeInfo() {
      const a = this.activity
      if (!a) return null
      const now = this.nowMs ? new Date(this.nowMs) : new Date()
      const nowMs = now.getTime()
      const startMs = this.parseMs(a.startTime)
      const endMs = this.parseMs(a.endTime)
      const ongoing = this.kind === 'running' || (endMs != null && nowMs < endMs)
      const untilMs = ongoing ? nowMs : endMs
      const elapsed = startMs == null || untilMs == null ? null : Math.max(0, untilMs - startMs)
      return {
        // 时刻与时长共用同一个 now，避免跨午夜时两者落在不同日期
        moment: this.formatMoment(ongoing ? a.startTime : a.endTime, now),
        ongoing,
        duration: elapsed == null ? '' : this.formatDuration(elapsed)
      }
    },
    /** 状态前缀（含冒号）：记录仍在区间内时提示「在做」，但括号标出真实类型 */
    label() {
      if (this.liveKind === 'running') return '在做：'
      if (this.liveKind === 'live-record') return '在做(记录)：'
      return '上次：'
    },
    /** 动态内容：空内容兜底为「未命名记录」 */
    content() {
      const a = this.activity
      if (!a) return ''
      return (a.content || '').trim() || '未命名记录'
    },
    /**
     * 第二行左半：开始 / 结束时刻。
     * - 还在区间内（正在做 / 计划未到结束时间）→「开始于 17:30」
     * - 已越过结束时间 →「结束于 17:30」
     */
    momentText() {
      const info = this.timeInfo
      if (!info || !info.moment) return ''
      return `${info.ongoing ? '开始于' : '结束于'}${info.moment}`
    },
    /** 第二行右半：已做 / 用时（极窄时先省略它，完整值在 title 里）。
     *  - 进行中（开始于，含正在做 / 未到结束时间）→「已用时」，因为任务还没做完
     *  - 已越过结束时间（结束于）→「用时」，任务已完成 */
    durationText() {
      const info = this.timeInfo
      if (!info || !info.duration) return ''
      return `${info.ongoing ? '已用时' : '用时'}${info.duration}`
    },
    /**
     * 第二行整串：`开始于17:30 | 用时1小时29分钟`；无可用时间戳时为空串。
     *
     * 只给 title 用，模板里拆成 momentText / durationText 两段渲染 ——
     * 中间那个「 | 」是 CSS 画的，这样窄栏省略时长时管道符不会跟着消失。
     */
    meta() {
      return [this.momentText, this.durationText].filter(Boolean).join(' | ')
    },
    /** 动态行完整文案（供 title 与无障碍朗读使用；label 自带冒号，内容紧跟其后） */
    text() {
      if (!this.activity) return ''
      return `${this.label}${this.content}${this.meta ? ` ${this.meta}` : ''}`
    },
    /** 悬浮完整信息：补上来源工作表 */
    fullText() {
      const a = this.activity
      if (!a) return ''
      const project = (a.projectName || '').trim()
      const suffix = project ? `（工作表：${project}）` : ''
      return this.text + suffix
    }
  },
  methods: {
    /**
     * 「在做」期间挂一个 30s 定时器推进 nowMs，让「已用时」实时增长，
     * 并在越过结束时间时自动从「在做(记录)」回落成「上次」；
     * 已结束的记录不挂 —— 时长是固定值，没必要为它起计时器。
     * 定时器句柄挂在实例上（不进 data），避免无意义的响应式依赖。
     */
    syncTicker() {
      if (this.liveKind === 'done') {
        this.stopTicker()
        return
      }
      if (this._tickTimer) return
      this.nowMs = Date.now()
      this._tickTimer = setInterval(() => {
        this.nowMs = Date.now()
      }, 30000)
    },
    stopTicker() {
      if (this._tickTimer) {
        clearInterval(this._tickTimer)
        this._tickTimer = null
      }
      // 归零 → 回到「用当前时刻算」的默认口径
      this.nowMs = 0
    },
    /** 任意时间戳 → 毫秒；空值 / 非法值返回 null */
    parseMs(value) {
      if (!value) return null
      const ms = new Date(value).getTime()
      return Number.isNaN(ms) ? null : ms
    },
    /**
     * 毫秒时长 → 文案：
     * - 不足 1 分钟 → 「不到1分钟」
     * - 不足 1 小时 → 「35分钟」
     * - 整小时 → 「2小时」，否则「1小时20分钟」
     */
    formatDuration(ms) {
      const totalMinutes = Math.floor(ms / MINUTE_MS)
      if (totalMinutes < 1) return '不到1分钟'
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (!hours) return `${minutes}分钟`
      return minutes ? `${hours}小时${minutes}分钟` : `${hours}小时`
    },
    /**
     * ISO 本地时间串 → 今天 HH:mm / 昨天 HH:mm / M月d日 HH:mm / YYYY年M月d日
     *
     * 当天的时刻不带「今天」二字：动态行所在的左栏只有约 20% 屏宽，
     * 多这两个字会把后面的时长挤到省略号外面，而时长才是这行的重点。
     * 跨天（昨天 / 更早 / 未来）仍然带上日期，信息不丢。
     *
     * @param {string} value 时间戳
     * @param {Date} now 统一的「此刻」基准，避免同一行里时刻与时长跨午夜错位
     */
    formatMoment(value, now) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      const base = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date()
      const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      const startOfToday = new Date(base.getFullYear(), base.getMonth(), base.getDate()).getTime()
      const startOfThatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
      // 用 round 而非 floor：跨夏令时的两天相差 23h / 25h，floor 会算成 0 天（误判「今天」）
      const diffDays = Math.round((startOfToday - startOfThatDay) / DAY_MS)
      if (diffDays === 0) return hm
      if (diffDays === 1) return `昨天 ${hm}`
      const md = `${d.getMonth() + 1}月${d.getDate()}日`
      return d.getFullYear() === base.getFullYear() ? `${md} ${hm}` : `${d.getFullYear()}年${md}`
    }
  }
}
</script>

<style scoped>
/* 动态行：跟随列表行的紧凑节奏（字号 12px、与名字间距 3px），颜色随状态区分。
   固定两行 —— 第一行「上次/正在做：内容」，第二行「开始/结束于 17:30 | 用时 1小时29分钟」，
   这样内容独占一行不必和时间抢宽度，时间也不会被内容挤成省略号。 */
.activity {
  display: flex;
  /* 两行结构下圆点垂直居中于整块，视觉上作为两行共同的项目符号 */
  align-items: center;
  min-width: 0;
  margin-top: 3px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.activity .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex: 0 0 auto;
  margin-right: 5px;
}

.activity .text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* 第一行：前缀 + 内容，内容超长时省略（完整内容在 title 里） */
.activity .head {
  display: flex;
  min-width: 0;
}

/* 状态前缀：固定宽度，永不收缩 */
.activity .label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.activity .body {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 第二行：时刻 | 时长，独占一行；极窄时先省略时长，时刻始终完整。
   column-gap 就是「竖线左右各一个空格」——比在文本里写空格可靠
   （前导空格在 flex item 里会被行首折叠掉）。 */
.activity .meta {
  display: flex;
  min-width: 0;
  column-gap: 4px;
  overflow: hidden;
}

.activity .meta .moment {
  flex: 0 0 auto;
  white-space: nowrap;
}

/* 竖线独立成段：窄栏省略时长时它不会跟着一起消失 */
.activity .meta .sep {
  flex: 0 0 auto;
}

.activity .meta .duration {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 正在做：主色绿 + 呼吸圆点，一眼看出「此刻在忙」 */
.activity-running {
  color: var(--lp-primary-dark, #2f7a4a);
}

.activity-running .dot {
  background: var(--lp-primary, #4aa46a);
  animation: activity-pulse 1.6s ease-in-out infinite;
}

@keyframes activity-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.8); }
}

/* 上一次：中性灰绿，弱化成历史信息，不与「正在做」抢注意力 */
.activity-done {
  color: var(--lp-text-3, #94a59b);
}

.activity-done .dot {
  background: #c3cfc6;
}

@media (prefers-reduced-motion: reduce) {
  .activity-running .dot {
    animation: none;
  }
}
</style>
